import React from "react";
import { FileText, Smartphone, Server, Cloud, PenLine, Activity, Sparkles } from "lucide-react";

// Map connector_mode to a category bucket displayed in the summary.
//   manual          -> "Manual"
//   csv_import      -> "CSV"
//   cloud_api       -> "Cloud"
//   mobile_local_sync -> "Mobile"
//   bridge          -> "Bridge"
const BUCKETS = [
  { key: "manual",            label: "Manual", Icon: PenLine,   color: "#A1A1AA" },
  { key: "csv_import",        label: "CSV",    Icon: FileText,  color: "#FAFAFA" },
  { key: "cloud_api",         label: "Cloud",  Icon: Cloud,     color: "#10B981" },
  { key: "mobile_local_sync", label: "Mobile", Icon: Smartphone, color: "#3B82F6" },
  { key: "bridge",            label: "Bridge", Icon: Server,    color: "#10B981" },
];

// Compute home-level Automation Level from connected sources.
//   Automated      — all connected sources push automatically (cloud_api or bridge)
//   Semi-automated — at least one automated, but some manual/csv/mobile remain
//   Manual         — every connected source is manual or csv_import only
//
// Exported so the Dashboard's "Home Intelligence" card can reuse the same
// rules — keeping the two summaries verbally consistent.
export function automationLevel(sources) {
  const connected = sources.filter((s) => s.status === "connected");
  if (connected.length === 0) {
    return { level: "Manual", tone: "muted",
      reason: "No connectors are reporting yet — start with one CSV import or Open-Meteo." };
  }
  const auto = connected.filter((s) => ["cloud_api", "bridge"].includes(s.connector_mode));
  const semi = connected.filter((s) => s.connector_mode === "mobile_local_sync");
  const handCranked = connected.filter((s) => ["manual", "csv_import"].includes(s.connector_mode));

  if (auto.length === connected.length) {
    return { level: "Automated", tone: "good",
      reason: "Every connected source pushes data on its own — no daily attention required." };
  }
  if (auto.length > 0 || semi.length > 0) {
    return { level: "Semi-automated", tone: "warn",
      reason: handCranked.length === 0
        ? "Some sources need a phone-on-network step; everything else is hands-off."
        : `${handCranked.length} source${handCranked.length === 1 ? "" : "s"} still need${handCranked.length === 1 ? "s" : ""} CSV imports. The rest run on their own.` };
  }
  return { level: "Manual", tone: "muted",
    reason: "All sources rely on CSV imports or hand-entered values today." };
}

const TONE_STYLES = {
  good:   { pill: "pill-connected", icon: "#10B981" },
  warn:   { pill: "pill-stale",     icon: "#F59E0B" },
  muted:  { pill: "pill-manual",    icon: "#A1A1AA" },
};

export default function ConnectorSummary({ sources }) {
  if (!sources || sources.length === 0) return null;

  const counts = BUCKETS.reduce((acc, b) => {
    acc[b.key] = sources.filter((s) => s.connector_mode === b.key).length;
    return acc;
  }, {});

  const level = automationLevel(sources);
  const tone = TONE_STYLES[level.tone];

  // Build a natural-language summary line, only including non-zero buckets.
  const parts = BUCKETS
    .filter((b) => counts[b.key] > 0)
    .map((b) => `${counts[b.key]} ${b.label}`);
  const summaryLine =
    parts.length === 0
      ? "No connectors yet."
      : parts.join(", ").replace(/, ([^,]*)$/, " and $1");

  return (
    <section className="card-base p-5" data-testid="connector-summary">
      <div className="flex items-start justify-between gap-4 flex-col sm:flex-row">
        <div className="min-w-0">
          <div className="label-tiny mb-1.5 flex items-center gap-2">
            <Sparkles className="w-3 h-3" /> Connector mix
          </div>
          <h2 className="font-heading text-xl font-semibold tracking-tight">
            Your home currently uses {summaryLine}.
          </h2>
          <p className="text-xs text-muted-foreground mt-1.5 max-w-xl leading-relaxed">{level.reason}</p>
        </div>

        <div className="flex flex-col items-start sm:items-end gap-1.5 flex-shrink-0">
          <div className="label-tiny flex items-center gap-1.5">
            <Activity className="w-3 h-3" /> Automation level
          </div>
          <span
            className={`pill ${tone.pill}`}
            data-testid="automation-level"
            data-level={level.level.toLowerCase()}
          >
            <span className="pill-dot" />
            {level.level}
          </span>
        </div>
      </div>

      {/* Per-bucket breakdown strip */}
      <div className="grid grid-cols-5 gap-2 mt-5">
        {BUCKETS.map((b) => {
          const n = counts[b.key];
          const dim = n === 0;
          return (
            <div
              key={b.key}
              className={`border border-border rounded-md p-3 ${dim ? "opacity-50" : ""}`}
              data-testid={`bucket-${b.key}`}
            >
              <div className="flex items-center gap-1.5 text-muted-foreground">
                <b.Icon className="w-3 h-3" style={{ color: dim ? undefined : b.color }} />
                <span className="text-[10px] uppercase tracking-wider">{b.label}</span>
              </div>
              <div className="font-heading text-2xl mt-1.5 font-semibold tabular-nums">{n}</div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

// Per-source mini badge — used inline on each Data Source row.
export function AutomationBadge({ mode, testid }) {
  const level = MODE_TO_LEVEL[mode] || MODE_TO_LEVEL.manual;
  const cls = {
    Manual:           "pill-manual",
    "Semi-automated": "pill-stale",
    Automated:        "pill-connected",
  }[level];
  return (
    <span className={`pill ${cls}`} data-testid={testid || "automation-badge"} data-level={level.toLowerCase()}>
      <span className="pill-dot" />
      {level}
    </span>
  );
}

const MODE_TO_LEVEL = {
  manual: "Manual",
  csv_import: "Semi-automated",
  mobile_local_sync: "Semi-automated",
  cloud_api: "Automated",
  bridge: "Automated",
};
